import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, Phone, ChevronDown, Wrench, Image, Info, Mail, Award, Shield, X, HelpCircle, ChevronRight, MapPin, Clock } from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import BoroughSelector from './BoroughSelector';

const HeaderContainer = styled.header<{ $isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${brandTheme.zIndex.sticky};
  background: ${props => props.$isScrolled 
    ? 'rgba(0, 0, 0, 0.9)' 
    : 'rgba(0, 0, 0, 0.3)'};
  backdrop-filter: ${props => props.$isScrolled ? 'blur(25px)' : 'blur(15px)'};
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  padding: ${props => props.$isScrolled ? '0.75rem 0' : '1.25rem 0'};
  border-bottom: ${props => props.$isScrolled 
    ? `1px solid rgba(254, 206, 27, 0.3)` 
    : '1px solid rgba(254, 206, 27, 0.2)'};
  box-shadow: ${props => props.$isScrolled 
    ? '0 8px 32px rgba(0, 0, 0, 0.3)' 
    : '0 4px 20px rgba(0, 0, 0, 0.1)'};

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    padding: ${props => props.$isScrolled ? '0.75rem 0' : '1rem 0'};
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    padding: ${props => props.$isScrolled ? '0.5rem 0' : '0.75rem 0'};
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    padding: ${props => props.$isScrolled ? '0.625rem 0' : '1rem 0'};
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    padding: ${props => props.$isScrolled ? '0.5rem 0' : '0.875rem 0'};
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    padding: ${props => props.$isScrolled ? '0.5rem 0' : '0.75rem 0'};
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: ${props => props.$isScrolled ? '0.375rem 0' : '0.625rem 0'};
  }
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${brandTheme.spacing[6]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 0 ${brandTheme.spacing[5]};
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    padding: 0 ${brandTheme.spacing[4]};
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    padding: 0 ${brandTheme.spacing[4]};
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    padding: 0 ${brandTheme.spacing[3]};
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    padding: 0 ${brandTheme.spacing[3]};
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0 ${brandTheme.spacing[2]};
  }
`;

const Logo = styled(Link)<{ $isScrolled: boolean }>`
  display: flex;
  align-items: center;
  gap: ${brandTheme.spacing[4]};
  text-decoration: none;
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  z-index: ${brandTheme.zIndex.sticky + 1};
  
  &:hover {
    transform: scale(1.02);
  }
  
  img {
    height: ${props => props.$isScrolled ? '45px' : '55px'};
    width: auto;
    transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));

    /* iPhone SE and Small Mobile */
    @media (max-width: 389px) and (min-width: 320px) {
      height: ${props => props.$isScrolled ? '35px' : '45px'};
    }

    /* Landscape orientation adjustments */
    @media (orientation: landscape) and (max-height: 500px) {
      height: ${props => props.$isScrolled ? '30px' : '40px'};
    }
  }

  .logo-text {
    display: flex;
    flex-direction: column;
    font-family: ${brandTheme.typography.fontFamily.heading};
    
    .company-name {
      font-size: ${props => props.$isScrolled ? '1.25rem' : '1.5rem'};
      font-weight: ${brandTheme.typography.fontWeight.bold};
      color: #ffffff;
      line-height: 1;
      transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
      letter-spacing: -0.025em;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    }
    
    .tagline {
      font-size: ${props => props.$isScrolled ? '0.75rem' : '0.875rem'};
      font-weight: ${brandTheme.typography.fontWeight.medium};
      color: #FECE1B;
      line-height: 1;
      margin-top: 2px;
      transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
      text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    }

    /* Hide logo text on mobile devices */
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${brandTheme.spacing[1]};

  /* Hide desktop nav on tablets and mobile */
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: relative;
  
  &:hover .mega-menu {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
  
  .mega-menu:hover {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
`;

const NavLink = styled(Link)<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${brandTheme.spacing[1]};
  padding: ${brandTheme.spacing[3]} ${brandTheme.spacing[4]};
  color: ${props => props.$isActive ? '#FECE1B' : 'rgba(255, 255, 255, 0.9)'};
  text-decoration: none;
  font-weight: ${brandTheme.typography.fontWeight.medium};
  font-size: 1rem;
  border-radius: ${brandTheme.borderRadius.lg};
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  
  &:hover {
    color: #FECE1B;
    background: rgba(254, 206, 27, 0.1);
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const MegaMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: ${props => props.$isOpen ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-10px)'};
  width: 1000px;
  max-width: 90vw;
  background: rgba(0, 0, 0, 0.98);
  backdrop-filter: blur(25px);
  border-radius: ${brandTheme.borderRadius['2xl']};
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(254, 206, 27, 0.2);
  border: 1px solid rgba(254, 206, 27, 0.4);
  padding: ${brandTheme.spacing[10]};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  transition-delay: ${props => props.$isOpen ? '0ms' : '100ms'};
  z-index: ${brandTheme.zIndex.dropdown};

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid rgba(254, 206, 27, 0.4);
  }

  @media (max-width: 1024px) {
    display: none;
  }

  @media (max-width: 1200px) {
    width: 900px;
    padding: ${brandTheme.spacing[8]};
  }
`;

const MegaMenuGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${brandTheme.spacing[10]};
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: ${brandTheme.spacing[8]};
  }
`;

const MegaMenuColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${brandTheme.spacing[6]};
`;

const MegaMenuSection = styled.div`
  h3 {
    font-size: 1.125rem;
    font-weight: ${brandTheme.typography.fontWeight.semibold};
    color: #FECE1B;
    margin-bottom: ${brandTheme.spacing[3]};
    display: flex;
    align-items: center;
    gap: ${brandTheme.spacing[2]};
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin-bottom: ${brandTheme.spacing[2]};
  }

  a {
    display: flex;
    align-items: center;
    gap: ${brandTheme.spacing[3]};
    padding: ${brandTheme.spacing[3]} ${brandTheme.spacing[4]};
    color: rgba(255, 255, 255, 0.9);
    text-decoration: none;
    border-radius: ${brandTheme.borderRadius.lg};
    transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
    font-size: 0.95rem;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    border: 1px solid transparent;

    &:hover {
      background: rgba(254, 206, 27, 0.1);
      color: #FECE1B;
      transform: translateX(4px);
      border-color: rgba(254, 206, 27, 0.2);
    }

    svg {
      width: 16px;
      height: 16px;
      color: #FECE1B;
      flex-shrink: 0;
    }

    .service-icon {
      font-size: 1.5rem;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(254, 206, 27, 0.1);
      border-radius: 50%;
      flex-shrink: 0;
    }

    .service-content {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .service-title {
      font-weight: 600;
      font-size: 1rem;
      color: rgba(255, 255, 255, 0.95);
    }

    .service-desc {
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.7);
      font-weight: 400;
    }
  }
`;

const MegaMenuImage = styled.div`
  position: relative;
  border-radius: ${brandTheme.borderRadius.xl};
  overflow: hidden;
  height: 220px;
  background: linear-gradient(135deg, ${brandTheme.colors.primary[50]} 0%, ${brandTheme.colors.primary[100]} 100%);
  border: 2px solid rgba(254, 206, 27, 0.2);
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${brandTheme.animation.duration.slow} ${brandTheme.animation.easing.smooth};
  }

  &:hover {
    border-color: rgba(254, 206, 27, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.2);
  }

  &:hover img {
    transform: scale(1.05);
  }

  .image-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.9));
    padding: ${brandTheme.spacing[5]};
    color: white;

    h4 {
      font-size: 1.25rem;
      font-weight: ${brandTheme.typography.fontWeight.semibold};
      margin-bottom: ${brandTheme.spacing[2]};
      color: #FECE1B;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    }

    p {
      font-size: 0.9rem;
      opacity: 0.95;
      line-height: 1.5;
      margin-bottom: ${brandTheme.spacing[2]};
    }

    .image-badge {
      display: inline-block;
      background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
      color: #000000;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${brandTheme.spacing[3]};

  @media (max-width: 1024px) {
    display: none;
  }
`;

const ContactButton = styled.a`
  display: flex;
  align-items: center;
  gap: ${brandTheme.spacing[2]};
  padding: ${brandTheme.spacing[3]} ${brandTheme.spacing[4]};
  border-radius: ${brandTheme.borderRadius.full};
  text-decoration: none;
  font-weight: ${brandTheme.typography.fontWeight.medium};
  font-size: 0.875rem;
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
`;

const PhoneLink = styled(ContactButton)`
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: #000000;
  box-shadow: 0 4px 15px rgba(254, 206, 27, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(254, 206, 27, 0.4);
  }
`;


const MobileMenuOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: ${brandTheme.zIndex.overlay};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};

  @media (min-width: 1025px) {
    display: none;
  }
`;

const MobileMenuContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 400px;
  height: 100vh;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.98) 0%, rgba(20, 20, 20, 0.98) 100%);
  backdrop-filter: blur(25px);
  border-left: 2px solid rgba(254, 206, 27, 0.3);
  box-shadow: -10px 0 50px rgba(0, 0, 0, 0.5);
  z-index: ${brandTheme.zIndex.modal};
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    max-width: 100%;
    border-left: none;
    border-top: 2px solid rgba(254, 206, 27, 0.3);
  }

  @media (min-width: 1025px) {
    display: none;
  }
`;

const MobileMenuContent = styled.div`
  padding: ${brandTheme.spacing[6]};
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${brandTheme.spacing[6]};

  @media (max-width: 375px) {
    padding: ${brandTheme.spacing[4]};
    gap: ${brandTheme.spacing[4]};
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${brandTheme.spacing[4]};
  border-bottom: 2px solid rgba(254, 206, 27, 0.2);
  margin-bottom: ${brandTheme.spacing[2]};

  .mobile-logo {
    display: flex;
    align-items: center;
    gap: ${brandTheme.spacing[3]};
    text-decoration: none;
    flex: 1;

    img {
      height: 45px;
      width: auto;
      filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
    }

    .logo-text {
      display: flex;
      flex-direction: column;
      font-family: ${brandTheme.typography.fontFamily.heading};
      
      .company-name {
        font-size: 1.25rem;
        font-weight: ${brandTheme.typography.fontWeight.bold};
        color: #ffffff;
        line-height: 1;
        letter-spacing: -0.025em;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      }
      
      .tagline {
        font-size: 0.75rem;
        font-weight: ${brandTheme.typography.fontWeight.medium};
        color: #FECE1B;
        line-height: 1;
        margin-top: 2px;
        text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
      }
    }
  }

  @media (max-width: 375px) {
    .mobile-logo {
      .logo-text {
        .company-name {
          font-size: 1.125rem;
        }
        
        .tagline {
          font-size: 0.7rem;
        }
      }
    }
  }
`;

const MobileMenuNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${brandTheme.spacing[1]};
  flex: 1;
`;

const MobileNavItem = styled.div`
  position: relative;
`;

const MobileNavLink = styled(Link)<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${brandTheme.spacing[3]};
  padding: ${brandTheme.spacing[4]} ${brandTheme.spacing[4]};
  color: ${props => props.$isActive ? '#FECE1B' : 'rgba(255, 255, 255, 0.9)'};
  text-decoration: none;
  font-weight: ${brandTheme.typography.fontWeight.medium};
  font-size: 1.125rem;
  border-radius: ${brandTheme.borderRadius.lg};
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  background: ${props => props.$isActive ? 'rgba(254, 206, 27, 0.1)' : 'transparent'};
  border: 1px solid ${props => props.$isActive ? 'rgba(254, 206, 27, 0.3)' : 'transparent'};
  min-height: 56px;
  
  &:hover {
    color: #FECE1B;
    background: rgba(254, 206, 27, 0.1);
    border-color: rgba(254, 206, 27, 0.2);
    transform: translateX(4px);
  }

  &:active {
    transform: translateX(2px) scale(0.98);
  }

  .nav-content {
    display: flex;
    align-items: center;
    gap: ${brandTheme.spacing[3]};
    flex: 1;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .chevron {
    width: 20px;
    height: 20px;
    transition: transform ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  }
`;

const MobileServicesSubmenu = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '600px' : '0'};
  overflow: hidden;
  transition: max-height ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  margin-left: ${brandTheme.spacing[4]};
  border-left: 2px solid rgba(254, 206, 27, 0.2);
  padding-left: ${brandTheme.spacing[4]};
  margin-top: ${brandTheme.spacing[2]};
  margin-bottom: ${brandTheme.spacing[2]};
`;

const MobileSubmenuItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${brandTheme.spacing[3]};
  padding: ${brandTheme.spacing[3]} ${brandTheme.spacing[4]};
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 1rem;
  border-radius: ${brandTheme.borderRadius.md};
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  margin-bottom: ${brandTheme.spacing[2]};
  min-height: 48px;

  &:hover {
    color: #FECE1B;
    background: rgba(254, 206, 27, 0.1);
    transform: translateX(4px);
  }

  &:active {
    transform: translateX(2px) scale(0.98);
  }

  .service-icon {
    font-size: 1.5rem;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(254, 206, 27, 0.1);
    border-radius: 50%;
    flex-shrink: 0;
  }

  .service-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
  }

  .service-title {
    font-weight: 600;
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.95);
  }

  .service-desc {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 400;
  }
`;

const MobileContactSection = styled.div`
  margin-top: auto;
  padding-top: ${brandTheme.spacing[6]};
  border-top: 2px solid rgba(254, 206, 27, 0.2);

  .contact-title {
    font-size: 1.25rem;
    font-weight: ${brandTheme.typography.fontWeight.semibold};
    color: #FECE1B;
    margin-bottom: ${brandTheme.spacing[4]};
    display: flex;
    align-items: center;
    gap: ${brandTheme.spacing[2]};
  }

  .contact-items {
    display: flex;
    flex-direction: column;
    gap: ${brandTheme.spacing[3]};
    margin-bottom: ${brandTheme.spacing[6]};
  }

  .contact-item {
    display: flex;
    align-items: center;
    gap: ${brandTheme.spacing[3]};
    padding: ${brandTheme.spacing[3]};
    background: rgba(254, 206, 27, 0.05);
    border-radius: ${brandTheme.borderRadius.lg};
    border: 1px solid rgba(254, 206, 27, 0.1);
    transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};

    &:hover {
      background: rgba(254, 206, 27, 0.1);
      border-color: rgba(254, 206, 27, 0.2);
      transform: translateX(4px);
    }

    svg {
      color: #FECE1B;
      flex-shrink: 0;
      width: 18px;
      height: 18px;
    }

    .contact-content {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .contact-label {
      font-size: 0.875rem;
      color: rgba(255, 255, 255, 0.7);
      font-weight: 400;
    }

    .contact-value {
      font-size: 1rem;
      color: #ffffff;
      font-weight: 600;
    }
  }

  .mobile-phone-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${brandTheme.spacing[2]};
    padding: ${brandTheme.spacing[4]};
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: #000000;
    text-decoration: none;
    border-radius: ${brandTheme.borderRadius.xl};
    font-weight: ${brandTheme.typography.fontWeight.semibold};
    font-size: 1.125rem;
    transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
    box-shadow: 0 4px 15px rgba(254, 206, 27, 0.3);
    min-height: 56px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(254, 206, 27, 0.4);
    }

    &:active {
      transform: translateY(0);
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #FECE1B;
  cursor: pointer;
  padding: ${brandTheme.spacing[2]};
  border-radius: ${brandTheme.borderRadius.lg};
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  min-height: 44px;
  min-width: 44px;
  position: relative;
  z-index: ${brandTheme.zIndex.sticky + 1};

  &:hover {
    background: rgba(254, 206, 27, 0.1);
    color: #FECE1B;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 375px) {
    padding: ${brandTheme.spacing[1]};
  }
`;

const MobileMenuCloseButton = styled.button`
  position: absolute;
  top: ${brandTheme.spacing[6]};
  right: ${brandTheme.spacing[6]};
  background: rgba(254, 206, 27, 0.1);
  border: 2px solid rgba(254, 206, 27, 0.3);
  color: #FECE1B;
  cursor: pointer;
  padding: ${brandTheme.spacing[3]};
  border-radius: 50%;
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  min-height: 48px;
  min-width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${brandTheme.zIndex.modal + 10};
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(254, 206, 27, 0.2);
    border-color: rgba(254, 206, 27, 0.5);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 375px) {
    top: ${brandTheme.spacing[4]};
    right: ${brandTheme.spacing[4]};
    min-height: 44px;
    min-width: 44px;
    padding: ${brandTheme.spacing[2]};
  }
`;


const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveMegaMenu(null);
    setIsMobileServicesOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    console.log('Mobile menu toggle clicked, current state:', isMobileMenuOpen);
    const newState = !isMobileMenuOpen;
    console.log('Setting mobile menu to:', newState);
    setIsMobileMenuOpen(newState);
    setIsMobileServicesOpen(false);
  };

  const toggleMobileServices = () => {
    setIsMobileServicesOpen(!isMobileServicesOpen);
  };

  const navItems = [
    {
      path: '/services',
      label: 'Services',
      icon: Wrench,
      megaMenu: 'services'
    },
    {
      path: '/gallery/',
      label: 'Gallery',
      icon: Image,
      simple: true
    },
    {
      path: '/about-us/',
      label: 'About',
      icon: Info,
      simple: true
    },
    {
      path: '/faq/',
      label: 'FAQ',
      icon: HelpCircle,
      simple: true
    },
    {
      path: '/contact/',
      label: 'Contact',
      icon: Mail,
      simple: true
    }
  ];

  return (
    <>
      <HeaderContainer $isScrolled={isScrolled}>
        <NavContainer>
          <Logo to="/" $isScrolled={isScrolled}>
            <img src="/logo.png" alt="Resin Surfaces London" />
            <div className="logo-text">
              <div className="company-name">Resin Surfaces London</div>
              <div className="tagline">Resin Driveways, Patios & Paving</div>
            </div>
          </Logo>

          <Nav>
            {navItems.map((item) => (
              <NavItem key={item.path}>
                {item.megaMenu ? (
                  <>
                    <NavLink
                      to={item.path}
                      $isActive={location.pathname.startsWith(item.path)}
                      onMouseEnter={() => setActiveMegaMenu(item.megaMenu)}
                      onMouseLeave={() => setActiveMegaMenu(null)}
                    >
                      <item.icon size={16} />
                      {item.label}
                      <ChevronDown size={16} />
                    </NavLink>
                    
                    <MegaMenu $isOpen={activeMegaMenu === item.megaMenu} className="mega-menu">
                      <MegaMenuGrid>
                        <MegaMenuColumn>
                          <MegaMenuSection>
                            <h3>
                              <Wrench size={20} />
                              Our Services
                            </h3>
                            <ul>
                              <li>
                                <Link to="/resin-driveways-london/">
                                  <div className="service-icon">🚗</div>
                                  <div className="service-content">
                                    <span className="service-title">Resin Driveways</span>
                                    <span className="service-desc">Durable, permeable driveways</span>
                                  </div>
                                </Link>
                              </li>
                              <li>
                                <Link to="/resin-patios-london/">
                                  <div className="service-icon">🪴</div>
                                  <div className="service-content">
                                    <span className="service-title">Resin Patios & Courtyards</span>
                                    <span className="service-desc">Outdoor living areas & terraces</span>
                                  </div>
                                </Link>
                              </li>
                              <li>
                                <Link to="/resin-pool-decks-london/">
                                  <div className="service-icon">🏊</div>
                                  <div className="service-content">
                                    <span className="service-title">Pool Decks</span>
                                    <span className="service-desc">Slip-resistant pool areas</span>
                                  </div>
                                </Link>
                              </li>
                              <li>
                                <Link to="/resin-paving-london/">
                                  <div className="service-icon">🧱</div>
                                  <div className="service-content">
                                    <span className="service-title">Resin Paving & Pathways</span>
                                    <span className="service-desc">Entrances, walkways & steps</span>
                                  </div>
                                </Link>
                              </li>
                              <li>
                                <Link to="/resin-commercial-london/">
                                  <div className="service-icon">🏢</div>
                                  <div className="service-content">
                                    <span className="service-title">Commercial</span>
                                    <span className="service-desc">Business & public spaces</span>
                                  </div>
                                </Link>
                              </li>
                            </ul>
                          </MegaMenuSection>
                        </MegaMenuColumn>
                        <MegaMenuColumn>
                          <MegaMenuImage>
                            <img src="/images/resin-surfaces-bound-work1.jpeg" alt="Resin Bound Surface" />
                            <div className="image-overlay">
                              <h4>Premium Quality</h4>
                              <p>Transform your outdoor space with our professional resin bound surfacing solutions.</p>
                              <span className="image-badge">Free Quote</span>
                            </div>
                          </MegaMenuImage>
                        </MegaMenuColumn>
                      </MegaMenuGrid>
                    </MegaMenu>
                  </>
                ) : (
                  <NavLink
                    to={item.path}
                    $isActive={location.pathname === item.path}
                  >
                    <item.icon size={16} />
                    {item.label}
                  </NavLink>
                )}
              </NavItem>
            ))}
          </Nav>

          <ContactInfo>
            <BoroughSelector />
            <PhoneLink href="tel:+447535935779">
              <Phone size={16} />
              07535 935 779
            </PhoneLink>
          </ContactInfo>

          <MobileMenuButton onClick={toggleMobileMenu}>
            <Menu size={24} />
          </MobileMenuButton>
        </NavContainer>
      </HeaderContainer>

      <MobileMenuOverlay 
        $isOpen={isMobileMenuOpen} 
        onClick={() => setIsMobileMenuOpen(false)}
      />
      
      <MobileMenuContainer $isOpen={isMobileMenuOpen}>
        <MobileMenuCloseButton onClick={() => setIsMobileMenuOpen(false)}>
          <X size={20} />
        </MobileMenuCloseButton>
        
        <MobileMenuContent>
          <MobileMenuHeader>
            <Link to="/" className="mobile-logo" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="/logo.png" alt="Resin Surfaces London" />
              <div className="logo-text">
                <div className="company-name">Resin Surfaces London</div>
                <div className="tagline">Resin Driveways, Patios & Paving</div>
              </div>
            </Link>
          </MobileMenuHeader>

          <MobileMenuNav>
            {navItems.map((item) => (
              <MobileNavItem key={item.path}>
                {item.megaMenu ? (
                  <>
                    <MobileNavLink
                      to={item.path}
                      $isActive={location.pathname.startsWith(item.path)}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleMobileServices();
                      }}
                    >
                      <div className="nav-content">
                        <item.icon size={20} />
                        {item.label}
                      </div>
                      <ChevronRight 
                        size={20} 
                        className="chevron"
                        style={{ 
                          transform: isMobileServicesOpen ? 'rotate(90deg)' : 'rotate(0deg)'
                        }} 
                      />
                    </MobileNavLink>
                    
                    <MobileServicesSubmenu $isOpen={isMobileServicesOpen}>
                      <MobileSubmenuItem to="/resin-driveways-london/" onClick={() => setIsMobileMenuOpen(false)}>
                        <div className="service-icon">🚗</div>
                        <div className="service-content">
                          <span className="service-title">Resin Driveways</span>
                          <span className="service-desc">Durable, permeable driveways</span>
                        </div>
                      </MobileSubmenuItem>
                      <MobileSubmenuItem to="/resin-patios-london/" onClick={() => setIsMobileMenuOpen(false)}>
                        <div className="service-icon">🪴</div>
                        <div className="service-content">
                          <span className="service-title">Resin Patios & Courtyards</span>
                          <span className="service-desc">Outdoor living & terraces</span>
                        </div>
                      </MobileSubmenuItem>
                      <MobileSubmenuItem to="/resin-pool-decks-london/" onClick={() => setIsMobileMenuOpen(false)}>
                        <div className="service-icon">🏊</div>
                        <div className="service-content">
                          <span className="service-title">Pool Decks</span>
                          <span className="service-desc">Slip-resistant pool areas</span>
                        </div>
                      </MobileSubmenuItem>
                      <MobileSubmenuItem to="/resin-paving-london/" onClick={() => setIsMobileMenuOpen(false)}>
                        <div className="service-icon">🧱</div>
                        <div className="service-content">
                          <span className="service-title">Resin Paving & Pathways</span>
                          <span className="service-desc">Walkways, steps & entrances</span>
                        </div>
                      </MobileSubmenuItem>
                      <MobileSubmenuItem to="/resin-commercial-london/" onClick={() => setIsMobileMenuOpen(false)}>
                        <div className="service-icon">🏢</div>
                        <div className="service-content">
                          <span className="service-title">Commercial</span>
                          <span className="service-desc">Business & public spaces</span>
                        </div>
                      </MobileSubmenuItem>
                    </MobileServicesSubmenu>
                  </>
                ) : (
                  <MobileNavLink
                    to={item.path}
                    $isActive={location.pathname === item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="nav-content">
                      <item.icon size={20} />
                      {item.label}
                    </div>
                  </MobileNavLink>
                )}
              </MobileNavItem>
            ))}
          </MobileMenuNav>

          <MobileContactSection>
            <div className="contact-title">
              <Phone size={20} />
              Get In Touch
            </div>
            <div className="contact-items">
              <div className="contact-item">
                <Phone size={18} />
                <div className="contact-content">
                  <div className="contact-label">Call Now</div>
                  <div className="contact-value">07535 935 779</div>
                </div>
              </div>
              <div className="contact-item">
                <MapPin size={18} />
                <div className="contact-content">
                  <div className="contact-label">Service Area</div>
                  <div className="contact-value">All London Boroughs</div>
                </div>
              </div>
              <div className="contact-item">
                <Clock size={18} />
                <div className="contact-content">
                  <div className="contact-label">Hours</div>
                  <div className="contact-value">Mon-Fri: 8AM-6PM</div>
                </div>
              </div>
            </div>
            <a href="tel:+447535935779" className="mobile-phone-button">
              <Phone size={20} />
              Call Now - 07535 935 779
            </a>
          </MobileContactSection>
        </MobileMenuContent>
      </MobileMenuContainer>
    </>
  );
};

export default Header;